// Where the toolbar's name callouts go: a row of boxes under the hovered
// section, in button order, each joined to its button by a leader. Keeping the
// row in button order is what keeps the leaders from crossing; the row never
// covers the section's own buttons, because it sits below them. When one row
// cannot fit the screen, the callouts deal out over more rows (calloutRows).

/** A callout to place: its button's centre x and the box's width. */
export interface CalloutItem { x: number; w: number }

/** A leader passing through a row it has no box in keeps this much of the row
 *  clear (a "slot"), so no box there can sit across it. */
export const CALLOUT_SLOT = 10;

/** The callouts' rows. With `rows` > 1, callout i goes in row i % rows; a row
 *  keeps a slot open for every leader on its way to a lower row, in button
 *  order, so the leaders pass between its boxes and nothing crosses. */
export interface CalloutRows {
  maxW: number;
  rows: number;
  rowOf: number[];
  /** Each callout's box left edge, in its own row. */
  lefts: number[];
  widths: number[];
  /** Per callout, the x where it passes each row above its own (slot centres). */
  passes: number[][];
}

/** Fewest rows first, then the widest text wrap at which every row fits in
 *  [edge, screenW − edge]. `widthsAt` gives the boxes' widths at a wrap. */
export function calloutRows(
  xs: number[], widthsAt: (maxW: number) => number[], screenW: number, edge: number, gap: number,
  wraps: number[] = [170, 120, 90, 70],
): CalloutRows {
  const avail = screenW - 2 * edge;
  const n = xs.length;
  const fits = (widths: number[], rows: number): boolean => {
    for (let r = 0; r < rows; r++) {
      let w = 0, k = 0;
      for (let i = 0; i < n; i++) {
        if (i % rows < r) continue;
        w += i % rows === r ? widths[i] : CALLOUT_SLOT;
        k++;
      }
      if (w + gap * Math.max(0, k - 1) > avail) return false;
    }
    return true;
  };
  let pick = { maxW: wraps[wraps.length - 1], rows: Math.max(1, n), widths: widthsAt(wraps[wraps.length - 1]) };
  search: for (let rows = 1; rows <= Math.max(1, n); rows++) {
    for (const maxW of wraps) {
      const widths = widthsAt(maxW);
      if (fits(widths, rows)) { pick = { maxW, rows, widths }; break search; }
    }
  }
  const { maxW, rows, widths } = pick;
  const rowOf = xs.map((_, i) => i % rows);
  const lefts = new Array<number>(n).fill(0);
  const passes: number[][] = xs.map(() => []);
  const want = [...xs];                 // where each leader arrives at the next row
  for (let r = 0; r < rows; r++) {
    const ids = xs.map((_, i) => i).filter((i) => rowOf[i] >= r);
    const placed = calloutRowLefts(ids.map((i) => ({ x: want[i], w: rowOf[i] === r ? widths[i] : CALLOUT_SLOT })), screenW, edge, gap);
    ids.forEach((i, k) => {
      if (rowOf[i] === r) lefts[i] = placed[k];
      else { want[i] = placed[k] + CALLOUT_SLOT / 2; passes[i].push(want[i]); }
    });
  }
  return { maxW, rows, rowOf, lefts, widths, passes };
}

/** Each box's left edge, in item order. A box wants to sit centred under its
 *  button; boxes that would touch become a cluster centred on what its members
 *  want, so a crowd spreads evenly either side of its buttons. No two boxes come
 *  closer than `gap`, and the row stays within [edge, screenW − edge] whenever
 *  it fits there at all. */
export function calloutRowLefts(items: CalloutItem[], screenW: number, edge: number, gap: number): number[] {
  type Cluster = { n: number; w: number; want: number };
  const clusters: Cluster[] = [];
  for (const it of items) {
    clusters.push({ n: 1, w: it.w, want: it.x - it.w / 2 });
    while (clusters.length > 1) {
      const b = clusters[clusters.length - 1], a = clusters[clusters.length - 2];
      if (a.want + a.w + gap <= b.want) break;
      const n = a.n + b.n;
      clusters.splice(clusters.length - 2, 2, { n, w: a.w + gap + b.w, want: (a.want * a.n + (b.want - a.w - gap) * b.n) / n });
    }
  }
  const lefts: number[] = [];
  let i = 0;
  for (const c of clusters) {
    let x = c.want;
    for (let k = 0; k < c.n; k++, i++) { lefts.push(x); x += items[i].w + gap; }
  }
  // Into the screen: pushed right off the left edge, then back left off the right.
  for (let k = 0; k < items.length; k++) lefts[k] = Math.max(lefts[k], k ? lefts[k - 1] + items[k - 1].w + gap : edge);
  for (let k = items.length - 1; k >= 0; k--) {
    lefts[k] = Math.min(lefts[k], k < items.length - 1 ? lefts[k + 1] - gap - items[k].w : screenW - edge - items[k].w);
  }
  return lefts;
}

/** How far below the section the row hangs: further the further a box sits from
 *  its button, so a long leader is not too flat to follow. */
export function calloutDrop(items: CalloutItem[], lefts: number[], min = 26, max = 110): number {
  const disp = Math.max(0, ...items.map((it, i) => Math.abs(lefts[i] + it.w / 2 - it.x)));
  return Math.max(min, Math.min(max, disp * 0.3));
}

/** Where a leader meets its box's top edge: straight above the button when the
 *  box is under it, else the nearest point kept `inset` px inside the box. */
export function calloutLeaderEnd(left: number, w: number, x: number, inset = 8): number {
  return Math.max(left + Math.min(inset, w / 2), Math.min(left + w - Math.min(inset, w / 2), x));
}
